import React, { useState, useEffect, useRef } from 'react';
import Typography from '@digital-hig/mui/dist/Typography';
import PropTypes from 'prop-types';
import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';
import produce from 'immer';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';

// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import SvgError from '@digital-hig/icon/lib/esm/display-icons/error';
import SvgComplete from '@digital-hig/icon/lib/esm/display-icons/complete';
import Waffle from '@digital-hig/icon/lib/esm/icons/waffle';
import QuizTypeMatchingMobile from './quizTypeMatchingMobile';
import { styleVariables } from '../common/constants';

const QuizTypeMatching = ({
  question, quizState, setQuizState, isEveryQuestionAnswered, answers, setAnswers, isSignedIn,
}) => {
  const [dragging, setDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // const [curDragTo, setCurDragTo] = useState(-1);
  const dragItem = useRef();
  const dragNode = useRef();
  const draggedFrom = useRef();
  // const touchRef = useRef({});

  const findIsMobile = () => {
    const windowWidth = window.innerWidth;
    if (windowWidth <= 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    findIsMobile();
    window.addEventListener('resize', findIsMobile);
    return () => window.removeEventListener('resize', () => findIsMobile);
  }, []);

  const displayErrorOrSuccess = () => {
    if (Object.hasOwnProperty.call(question, 'isCorrect')) {
      if (!question.isCorrect) {
        return (
          <Grid className="answer-error">
            <SvgIcon size="medium"><SvgError /></SvgIcon>
            <Typography component="span" variant="body-copy-small" color="ink-error">
              {`Incorrect. ${question.explanation}`}
            </Typography>
          </Grid>
        );
      }
      return (
        <Grid className="answer-success">
          <SvgIcon size="medium"><SvgComplete /></SvgIcon>
          <Typography component="span" variant="body-copy-small">
            Nice work
          </Typography>
        </Grid>
      );
    }

    return <></>;
  };

  const handleDragOver = (e, group) => {
    const evt = e;
    evt.preventDefault();
    if (evt.target.innerHTML === '' && group === 'premise') {
      evt.target.classList.add('dragOverBg');
    }
  };

  const handleDragEnd = (e, group) => {
    setDragging(false);
    // setCurDragTo(-1);
    if (group === 'premise') {
      e.target.parentNode?.classList.add('dropped');
    }
    if (dragNode.current) {
      dragNode.current.removeEventListener('dragend', handleDragEnd);
    }
    dragItem.current = null;
    dragNode.current = null;
  };

  const handleOnDragStart = (e, dragId, groupFrom = '') => {
    if (!quizState.showReset) {
      const evt = e;
      evt.target.classList.add('dragStartBg');
      dragItem.current = dragId;
      dragNode.current = e.target;
      draggedFrom.current = groupFrom;
      evt.target.parentNode.classList.remove('dropped');
      dragNode.current.addEventListener('dragend', (ev) => { handleDragEnd(ev, groupFrom); });
      setTimeout(() => setDragging(true), 0);
    }
  };

  const isMatchingProblemAnswered = (updatedQuizState, questionId) => {
    let areAllTermsHasResponse;
    updatedQuizState.questions.forEach((q) => {
      if (q.id === questionId) {
        areAllTermsHasResponse = q.terms.filter((term) => term.response === undefined);
      }
    });
    return areAllTermsHasResponse.length === 0;
  };

  const handleMatchDrop = (e, quesn, droppedId, draggedTo) => {
    if (quizState.showReset) {
      return;
    }
    const evt = e;
    evt.stopPropagation();
    let updatedAnswers;
    // console.log('dragid', dragItem.current);
    // console.log('dropid', droppedId);
    // console.log('draggedFrom', draggedFrom.current);
    // console.log('draggedTo', draggedTo);

    if (draggedTo === 'premise') {
      evt.target.classList.remove('dragOverBg');
      evt.target.classList.add('dropped');
    }

    const updatedQuizState = produce(quizState, (draftState) => {
      if (draftState.questions) {
        draftState.questions.forEach((que) => {
          const q = que;
          if (q.id === quesn.id) {
            const isResAvailableInTerm = q.terms.find((term) => term.id === droppedId);
            const currentDraggedRes = q.answers.filter((ans) => ans.id === dragItem.current)[0];

            if (draggedTo === 'premise' && currentDraggedRes) {
              q.terms.find((term) => term.id === droppedId).response = currentDraggedRes;
            }

            if (draggedTo === 'response' && isResAvailableInTerm) {
              delete isResAvailableInTerm.response;
            }

            if (draggedFrom.current === 'premise' && draggedTo === 'premise' && dragItem.current !== droppedId) {
              const premise = q.terms.find((term) => term.id === dragItem.current);
              q.terms.find((term) => term.id === droppedId).response = premise?.response;
              delete premise.response;
            }
          }
        });
      }
    });
    setQuizState(updatedQuizState);

    if (isMatchingProblemAnswered(updatedQuizState, quesn.id)) {
      let matchingAns = {};
      updatedQuizState.questions.forEach((q) => {
        if (q.id === quesn.id) {
          matchingAns = {};
          q.terms.forEach((term) => {
            if ('response' in term) {
              matchingAns[term.id] = term.response.id;
            }
          });
        }
      });

      updatedAnswers = produce(answers, (draftState) => {
        draftState[question.id] = [matchingAns];
      });
    } else {
      updatedAnswers = produce(answers, (draftState) => {
        if (question.id in draftState) {
          delete draftState[question.id];
        }
      });
    }
    setAnswers(updatedAnswers);

    isEveryQuestionAnswered(updatedAnswers, updatedQuizState);
  };

  const handleDragLeave = (e) => {
    const evt = e;
    evt.preventDefault();
    evt.target.classList.remove('dragOverBg');
  };

  return (
    <RootElement>
      <Grid id={`qid${question.id}`} className={`${quizState.showUnanswered ? 'showUnanswered' : ''}`} container flexDirection="column" key={question.id}>
        <Grid item lg={6} md={9} className={`${quizState.unansweredQuestions.includes(question.id) ? 'unanswered' : 'answered'}`}>
          <Grid className="question-title">
            <Typography component="h4" variant="header-copy-small" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.text) }} />
          </Grid>
          { !isMobile
            ? (
              <Grid className="matching-problem">
                <Grid id={`${question.id}-premise-container`} className="premise-container" data-isSignedIn={!!isSignedIn}>
                  {question?.terms?.map((term) => (
                    <Grid className="item-container">
                      <Typography component="span" variant="header-copy-small" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(term.text) }} />
                      <Grid
                        className="premise-block"
                        id={term.id}
                        onDragOver={(e) => handleDragOver(e, 'premise')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleMatchDrop(e, question, term.id, 'premise')}
                      >
                        {term?.response
                      && (
                        <div
                          draggable={!!isSignedIn}
                          className={dragging ? `${(term.id === dragItem.current) ? 'hide' : ''} item` : 'item'}
                          onDragStart={(e) => handleOnDragStart(e, term.id, 'premise')}
                        >
                          <SvgIcon size="medium"><Waffle /></SvgIcon>
                          <span className="answer-text">{term?.response?.text}</span>
                        </div>
                      )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid
                  className="response-container"
                  id={`${question.id}-response-container`}
                  data-isSignedIn={!!isSignedIn}
                  onDragOver={(e) => handleDragOver(e, 'response')}
                  onDrop={(e) => handleMatchDrop(e, question, dragItem.current, 'response')}
                >
                  {question.answers.map((answer) => (
                    question?.terms?.findIndex((term) => term.response?.id === answer.id) === -1
                && (
                  <div className={dragging ? `${(answer.id === dragItem.current) ? 'hide' : ''} item-container` : 'item-container'}>
                    <div
                      className="item"
                      draggable={!!isSignedIn}
                      onDragStart={(e) => handleOnDragStart(e, answer.id, 'response')}
                      key={answer.id}
                      id={answer.id}
                    >
                      <div className="item-data">
                        <SvgIcon size="medium"><Waffle /></SvgIcon>
                        <div className="answer-text">{answer.text}</div>
                      </div>
                    </div>
                  </div>
                )
                  ))}
                </Grid>
              </Grid>
            )
            : (
              <QuizTypeMatchingMobile
                isSignedIn={isSignedIn}
                question={question}
                setQuizState={setQuizState}
                answers={answers}
                setAnswers={setAnswers}
                quizState={quizState}
                isEveryQuestionAnswered={isEveryQuestionAnswered}
              />
            )}
          {displayErrorOrSuccess()}
        </Grid>
        <Grid item className="error">
          <SvgIcon size="medium"><SvgError /></SvgIcon>
          <Typography component="span" className="error" variant="body-copy-small" color="ink-error">Select an answer</Typography>
        </Grid>
      </Grid>
    </RootElement>
  );
};

const RootElement = styled.div`
margin-top: 30px;
  .error {
    padding-top: 0;
  }
  .answered + .error, .unanswered + .error{
    display: none;
  }
  .showUnanswered {
    .unanswered {
      border: 1px solid red;
      padding: 12px !important;
      margin: 22px 0 8px 22px;
      border-radius: 6px;
    }
    .unanswered + .error {
      display: block;
      padding-top: 0;
    }
  }
  .answer-error, .answer-success{
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 18px;
    margin-top: -4px;
  }


.response-container{
  display: flex;
  flex-direction: column;
  flex: 1;

  .item-container{
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      background-color: white;
  .item {
      flex: 1;
      padding: 12px 16px 12px 12px;
      border: 1px solid rgba(204, 204, 204, 0.25);
      border-radius: 6px;
      box-shadow: 0 1px 0px rgba(102, 102, 102, 0.8);
      background-color: #ffffff;
      touch-action: none;
      margin-left: 22px;

      .answer-text {
          margin-left: 10px;
          font-size: 14px;
      }
      :hover {
          background: radial-gradient(65.63% 63.74% at 91.94% 0%, rgba(128, 128, 255, 0.15) 0%, rgba(128, 128, 255, 0) 100%);
          border: 1px solid rgba(204, 204, 204, 0.25);
          box-shadow: none;
          box-sizing: border-box;
        }
      .item-data {
          display: flex;
          align-items: center;
      }
    }
  }
  .bg {
      background-color: ${styleVariables.backgroundPanelColor} !important;
      box-shadow: none !important;
      border: 1px solid ${styleVariables.backgroundPanelColor} !important;
  }
  
}
.matching-problem {
    display: flex;
    flex-direction: row;
    margin-top: 20px;
    user-select: none;
    
  .premise-block{
      width: 308px;
      min-height: 52px;
      flex-grow: 0;
      margin: 8px 0 0;
      border-radius: 6px;
      border: dashed 1px ${styleVariables.inkOnBackground};

      .item { 
          display: flex;
          align-items: center;
          flex: 1;
          padding: 12px !important;
         
          border-radius: 6px;

          .answer-text {
              margin-left: 10px;
              font-size: 14px;
          }

          :hover {
              border: solid 2px rgba(95, 96, 255, 1);
              box-shadow: none;
              box-sizing: border-box;
              padding: 11px !important;
            }
      }
      
  }
  .response-container {
      .item {
          margin: 0 !important;
      }
  }
  .premise-container {
      margin-right: 30px;
      flex: 1;
      .item-container {
          margin-bottom: 20px;
          font-size: 14px;
      }
      &[data-isSignedIn="false"]{
        opacity: 0.4;
      }
  }
  .response-container{
      background-color: ${styleVariables.backgroundPanelColor};
      padding: 16px;
      width: 360px;
      border-radius: 6px;

      .item-container {
          .item {
              border: 1px solid #f9f9f9;
              display:flex;
              align-items: center;
              :hover{
                  border: solid 2px rgba(95, 96, 255, 1);
                  box-shadow: none;
                  box-sizing: border-box;
                  padding: 11px;
              }
              &[draggable="false"] {
                opacity: 0.4;
              }
          }
      }
  }  
}
.dragOverBg {
  background-color: ${styleVariables.backgroundPanelColor};
}
.dragStartBg {
  background-color: #ffffff;
}
.dropped {
    border: none !important;
    .item {
      border: dashed 1px ${styleVariables.inkOnBackground};
    }
}

.hide {
  opacity: 0;
}

`;

QuizTypeMatching.propTypes = {
  question: PropTypes.arrayOf({}).isRequired,
  answers: PropTypes.shape({}).isRequired,
  isEveryQuestionAnswered: PropTypes.func.isRequired,
  setAnswers: PropTypes.func.isRequired,
  isSignedIn: PropTypes.bool.isRequired,
  quizState: PropTypes.shape({
    showReset: PropTypes.bool,
    unansweredQuestions: PropTypes.arrayOf(),
    showUnanswered: PropTypes.string,
  }).isRequired,
  setQuizState: PropTypes.func.isRequired,
};

export default React.memo(QuizTypeMatching);
